import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface OrientationInfo {
  isLandscape: boolean;
  isMobile: boolean;
  angle: number;
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root',
})
export class OrientationService implements OnDestroy {
  private orientationSubject = new BehaviorSubject<OrientationInfo>(
    this.getOrientationInfo()
  );

  public orientation$ = this.orientationSubject.asObservable().pipe(
    debounceTime(100),
    distinctUntilChanged(
      (a, b) =>
        a.isLandscape === b.isLandscape && a.isMobile === b.isMobile
    )
  );

  private eventListeners: (() => void)[] = [];
  private resizeObserver?: ResizeObserver;

  constructor() {
    this.setupOrientationDetection();
  }


  private isActualMobileDevice(): boolean {
    const hasTouch = navigator.maxTouchPoints > 1;
    const mobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    const smallPhysicalScreen = Math.min(screen.width, screen.height) <= 768;
    return hasTouch && mobileUA && smallPhysicalScreen;
  }

  private getOrientationInfo(): OrientationInfo {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const angle = (screen.orientation?.angle ?? (window as any).orientation ?? 0) as number;

    return {
      isLandscape: height < width,
      isMobile: this.isActualMobileDevice(), 
      angle,
      width,
      height,
    };
  }

  private setupOrientationDetection() {
    const handleChange = () => {
      this.orientationSubject.next(this.getOrientationInfo());
    };

    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleChange);
      this.eventListeners.push(() =>
        screen.orientation.removeEventListener('change', handleChange)
      );
    }

    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', handleChange);
    this.eventListeners.push(() => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(handleChange);
      this.resizeObserver.observe(document.documentElement);
    }
  }

  getCurrentOrientation(): OrientationInfo {
    return this.orientationSubject.value;
  }

  isPortraitRecommended(): boolean {
    const info = this.getCurrentOrientation();
    return info.isMobile && info.isLandscape;
  }

  ngOnDestroy() {
    this.eventListeners.forEach((cleanup) => cleanup());
    this.resizeObserver?.disconnect();
    this.orientationSubject.complete();
  }
}